import { useState } from 'react';
import client from '../api/client';
import { messaggioErrore } from '../utils/apiError';
import FraseAlexa, { useParla } from './FraseAlexa';

// I campi di una capacità, costruiti dal suo schema (GET /v1/capacita/): la
// stessa schermata servirà a qualunque capacità che dichiari ingressi e
// risultati, non solo al meteo. Blocco 2b del piano in
// docs/REFACTOR_AUTOMAZIONI_UX_CODEX.md.
const field = 'w-full border rounded-lg px-3 py-2 bg-surface text-sm';
const TESTO_DEL_MESSAGGIO = '{{text}}';
const NUMERICI = ['gte', 'gt', 'lte', 'lt', 'eq', 'ne'];
const TESTUALI = ['contains', 'eq', 'ne'];
const numerico = campo => ['numero', 'intero'].includes(campo?.tipo);

export const campiMostrati = (schema, inputs) => (inputs.mostra
  ? inputs.mostra.split(',')
  : schema.risultati.filter(r => r.mostrato).map(r => r.id));

// «23 °C», «75%», «non disponibile»: come il server compone la frase.
export function valoreLeggibile(campo, valore) {
  if (valore === null || valore === undefined || valore === '') return 'non disponibile';
  const unita = campo.unita || '';
  const numero = typeof valore === 'number' && ['°C', 'km/h'].includes(unita) ? Math.round(valore) : valore;
  return `${numero}${unita === '' || unita === '%' ? '' : ' '}${unita}`;
}

function Ingresso({ ingresso, valore, onChange, predefinito }) {
  if (ingresso.tipo === 'vero_falso') {
    return <label className="flex items-start gap-2 text-sm">
      <input type="checkbox" className="mt-1" checked={valore ?? ingresso.predefinito ?? false} onChange={e => onChange(e.target.checked)} />
      <span>{ingresso.etichetta}{ingresso.descrizione && <span className="block text-xs text-muted">{ingresso.descrizione}</span>}</span>
    </label>;
  }
  const etichetta = predefinito ? `${ingresso.etichetta} predefinit${ingresso.id === 'giorno' ? 'o' : 'a'}` : ingresso.etichetta;
  if (ingresso.scelte) {
    return <label className="block">{etichetta}
      <select className={field} value={valore ?? ingresso.predefinito ?? ''}
        onChange={e => onChange(ingresso.scelte.every(s => typeof s.valore === 'number') ? Number(e.target.value) : e.target.value)}>
        {ingresso.scelte.map(s => <option key={s.valore} value={s.valore}>{s.etichetta}</option>)}
      </select>
      {ingresso.descrizione && <span className="text-xs text-muted">{ingresso.descrizione}</span>}
    </label>;
  }
  if (ingresso.tipo === 'intero' || ingresso.tipo === 'numero') {
    return <label className="block">{etichetta}
      <input type="number" className={field} required={ingresso.obbligatorio} min={ingresso.minimo}
        max={ingresso.massimo} value={valore ?? ''} onChange={e => onChange(e.target.value === '' ? undefined : Number(e.target.value))} />
    </label>;
  }
  // Con un messaggio la città può mancare qui, se si scrive sempre nel comando.
  return <label className="block">{etichetta}
    <input className={field} required={ingresso.obbligatorio && !predefinito} placeholder={`Es. ${ingresso.esempio}`}
      value={valore || ''} onChange={e => onChange(e.target.value)} />
    {ingresso.descrizione && !predefinito && <span className="text-xs text-muted">{ingresso.descrizione}</span>}
  </label>;
}

// La frase di «Fai parlare Alexa»: scritta qui, o una frase salvata, collegata (04/10/2026: «anche nella
// regola»): cambiandola nella pagina Parla con Alexa cambia anche qui.
function FraseDellaRegola({ inputs, setInputs, conMessaggio }) {
  const frasi = useParla()?.frasi || [];
  const salvata = inputs.frase_salvata ? frasi.find(f => f.id === inputs.frase_salvata) : null;
  return <div className="space-y-2">
    {(frasi.length > 0 || inputs.frase_salvata) && <label className="block text-sm">Frase salvata
      <select className={field} value={inputs.frase_salvata || ''}
        onChange={e => setInputs({ frase_salvata: e.target.value ? Number(e.target.value) : undefined, frase: e.target.value ? '' : inputs.frase })}>
        <option value="">Nessuna: la scrivo qui</option>
        {frasi.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
        {inputs.frase_salvata && !salvata && <option value={inputs.frase_salvata}>(frase eliminata)</option>}
      </select>
    </label>}
    {inputs.frase_salvata
      ? <p className="rounded-lg border px-3 py-2 text-sm">
        {salvata ? salvata.frase : 'Questa frase salvata non c’è più: scegline un’altra.'}
        <span className="block text-xs text-muted">Se la cambi in «Parla con Alexa», cambia anche qui.</span>
      </p>
      : <FraseAlexa valore={inputs.frase === TESTO_DEL_MESSAGGIO ? '' : inputs.frase} voce={inputs.voce || ''} salva
        vuota={conMessaggio ? 'Vuota: Alexa dirà il testo del messaggio.' : ''} onChange={valore => setInputs({ frase: valore })} />}
  </div>;
}

// «Quali righe» di un elenco (la classifica, le sfide): tutte, le prime N, o
// quella dove una colonna vale qualcosa, di solito la mia squadra.
function RigheScelta({ schema, inputs, setInputs }) {
  const colonne = schema.risultati.filter(r => r.colonna);
  const modo = inputs.righe || 'tutte';
  const squadra = schema.parametri?.squadra;
  const scegli = valore => setInputs(valore === 'tutte' ? { righe: 'tutte', quante: undefined, dove_campo: undefined, dove_valore: undefined }
    : valore === 'prime' ? { righe: 'prime', quante: inputs.quante || 5, dove_campo: undefined, dove_valore: undefined }
      : { righe: 'dove', quante: undefined, dove_campo: inputs.dove_campo || colonne.find(c => c.tipo === 'testo')?.percorso || colonne[0]?.percorso,
        dove_valore: inputs.dove_valore || (squadra ? '{{squadra}}' : '') });
  return <fieldset className="border rounded-lg p-3 space-y-2">
    <legend className="px-1 font-medium">Quali righe</legend>
    {[['tutte', 'Tutte'], ['prime', 'Le prime'], ['dove', 'La riga dove…']].map(([valore, etichetta]) => <label key={valore} className="flex items-center gap-2 text-sm">
      <input type="radio" name="quali-righe" checked={modo === valore} onChange={() => scegli(valore)} />{etichetta}
      {valore === 'prime' && modo === 'prime' && <input type="number" min="1" max="40" aria-label="Quante righe" className={`${field} w-20`}
        value={inputs.quante || 5} onChange={e => setInputs({ quante: Number(e.target.value) || 1 })} />}
    </label>)}
    {modo === 'dove' && <div className="grid sm:grid-cols-2 gap-2 pl-6">
      <label className="block text-sm">Colonna
        <select className={field} value={inputs.dove_campo || ''} onChange={e => setInputs({ dove_campo: e.target.value })}>
          {colonne.map(c => <option key={c.percorso} value={c.percorso}>{c.etichetta}</option>)}
        </select>
      </label>
      <label className="block text-sm">Vale
        {inputs.dove_valore === '{{squadra}}'
          ? <span className={`${field} flex justify-between items-center`}>la mia squadra ({squadra || 'da impostare nel menu Servizi'})
            <button type="button" className="text-xs underline" onClick={() => setInputs({ dove_valore: '' })}>cambia</button></span>
          : <input required className={field} value={inputs.dove_valore || ''} onChange={e => setInputs({ dove_valore: e.target.value })} />}
      </label>
      {squadra && inputs.dove_valore !== '{{squadra}}' && <button type="button" className="text-xs underline text-left" onClick={() => setInputs({ dove_valore: '{{squadra}}' })}>Usa la mia squadra ({squadra})</button>}
    </div>}
  </fieldset>;
}

export default function CapacitaCampi({ azione, schema, operatori, draft, change, usaMessaggi }) {
  const [prova, setProva] = useState(null);
  const [errore, setErrore] = useState('');
  const [inCorso, setInCorso] = useState(false);
  const [esempio, setEsempio] = useState('');
  const first = draft.steps[0];
  const inputs = first.inputs || {};
  const indiceCondizione = draft.steps.findIndex(s => s.action === 'condition.check');
  const condizione = indiceCondizione >= 0 ? draft.steps[indiceCondizione].inputs : null;
  const risultati = schema.risultati;
  const mostrabili = risultati.filter(r => r.mostrabile);
  const mostrati = campiMostrati(schema, inputs);
  const predefiniti = risultati.filter(r => r.mostrato).map(r => r.id);
  const perId = Object.fromEntries(risultati.map(r => [r.id, r]));
  // Nella condizione: i dati in cima, e le colonne solo se si sceglie una riga.
  const condizionabili = risultati.filter(r => (r.mostrabile || r.condizionabile || r.id === 'righe_trovate')
    && (!r.colonna || inputs.righe === 'dove'));

  const passi = aggiorna => change({ steps: aggiorna(draft.steps) });
  const setInputs = patch => { setProva(null); passi(steps => steps.map((s, i) => (i === 0 ? { ...s, inputs: { ...s.inputs, ...patch } } : s))); };
  // Città e giorno sono i predefiniti: se il messaggio li dice valgono quelli
  // del messaggio (29/09/2026). Il messaggio stesso è un ingresso nascosto.
  const conMessaggio = usaMessaggi && schema.ingressi.some(i => i.nascosto && i.predefinito === TESTO_DEL_MESSAGGIO);
  // «Fatti aiutare dall'AI» serve solo a leggere un messaggio.
  const visibili = schema.ingressi.filter(i => !i.nascosto && (!i.solo_con_messaggio || conMessaggio));
  const setCondizione = patch => { setProva(null); passi(steps => steps.map((s, i) => (i === indiceCondizione ? { ...s, inputs: { ...s.inputs, ...patch } } : s))); };

  const spunta = (id, acceso) => {
    const scelti = mostrabili.map(r => r.id).filter(c => (c === id ? acceso : mostrati.includes(c)));
    if (!scelti.length) return;
    // La scelta di partenza resta «nessuna scelta»: la frase completa della capacità.
    const uguali = scelti.length === predefiniti.length && scelti.every(c => predefiniti.includes(c));
    setInputs({ mostra: uguali ? undefined : scelti.join(',') });
  };

  const attivaCondizione = acceso => {
    setProva(null);
    passi(steps => (acceso
      ? [steps[0], { action: 'condition.check', inputs: { step: 1, ...(schema.condizione_suggerita || { field: condizionabili.find(numerico)?.id, operator: 'gte', value: 0 }) } }, ...steps.slice(1)]
      : steps.filter(s => s.action !== 'condition.check')));
  };
  const campoCondizione = condizione ? perId[condizione.field] : null;
  const ammessi = numerico(campoCondizione) ? NUMERICI : TESTUALI;
  const cambiaCampo = id => {
    const nuovo = perId[id];
    const operatore = (numerico(nuovo) ? NUMERICI : TESTUALI).includes(condizione.operator) ? condizione.operator : (numerico(nuovo) ? 'gte' : 'contains');
    setCondizione({ field: id, operator: operatore, value: numerico(nuovo) === numerico(campoCondizione) ? condizione.value : '' });
  };

  const provaDati = async () => {
    setErrore(''); setProva(null); setInCorso(true);
    const reali = Object.fromEntries(Object.entries(inputs).map(([k, v]) => [k, v === TESTO_DEL_MESSAGGIO ? esempio : v]));
    try {
      const { data } = await client.post('/v1/capacita/prova/', {
        azione, inputs: reali,
        ...(condizione ? { condizione: { field: condizione.field, operator: condizione.operator, value: condizione.value } } : {}),
      }, { timeout: 30000 });
      setProva(data);
    } catch (e) { setErrore(messaggioErrore(e, 'Prova non riuscita.')); }
    finally { setInCorso(false); }
  };

  // Una capacità che scrive (la lista della spesa) si prova senza messaggio:
  // la prova mostra i dati come sono e non cambia niente.
  const scrive = schema.effetti === 'scrittura';
  const usaEsempio = conMessaggio && !scrive;
  return <div className="space-y-4">
    {conMessaggio && schema.nota_messaggio && <p className="text-sm text-muted">{schema.nota_messaggio}</p>}
    {schema.elenco && <RigheScelta schema={schema} inputs={inputs} setInputs={setInputs} />}
    {visibili.map(ingresso => (azione === 'home.speak' && ingresso.id === 'frase'
      // La frase per Alexa si compone a pezzi: voci, toni, pause, suoni (03/10/2026), o è una frase salvata.
      ? <FraseDellaRegola key="frase" inputs={inputs} setInputs={setInputs} conMessaggio={conMessaggio} />
      : <Ingresso key={ingresso.id} ingresso={ingresso} valore={inputs[ingresso.id] === TESTO_DEL_MESSAGGIO ? '' : inputs[ingresso.id]}
        predefinito={conMessaggio} onChange={valore => setInputs({ [ingresso.id]: valore })} />))}

    {mostrabili.length > 0 && <fieldset className="border rounded-lg p-3 space-y-1">
      <legend className="px-1 font-medium">Cosa mostrare nel messaggio</legend>
      <p className="text-xs text-muted mb-1">
        {risultati.some(r => r.identifica) && `${risultati.filter(r => r.identifica).map(r => r.etichetta.toLowerCase()).join(' e ')} ci sono sempre. `}
        Un dato non spuntato si può comunque usare nella condizione qui sotto.
      </p>
      {mostrabili.map(r => <label key={r.id} className="flex items-center gap-2 text-sm">
        {/* Il nome della spunta è il solo dato; il valore (d'esempio o della
            prova) si annuncia come descrizione. */}
        <input type="checkbox" checked={mostrati.includes(r.id)} onChange={e => spunta(r.id, e.target.checked)}
          aria-label={r.etichetta} aria-describedby={`valore-${azione}-${r.id}`}
          disabled={mostrati.length === 1 && mostrati.includes(r.id)} />
        <span className="flex-1">{r.etichetta}</span>
        <span id={`valore-${azione}-${r.id}`} className="text-xs text-muted">
          {prova && !(r.colonna && inputs.righe !== 'dove') ? valoreLeggibile(r, prova.risultati[r.id]) : `es. ${valoreLeggibile(r, r.esempio)}`}
        </span>
      </label>)}
    </fieldset>}

    <fieldset className="border rounded-lg p-3 space-y-2">
      <legend className="px-1 font-medium">Continua solo se…</legend>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={!!condizione} onChange={e => attivaCondizione(e.target.checked)} />
        Manda il messaggio solo quando un dato lo richiede
      </label>
      {condizione && <div className="grid sm:grid-cols-3 gap-2">
        <label className="block text-sm">Dato
          <select className={field} value={condizione.field} onChange={e => cambiaCampo(e.target.value)}>
            {condizionabili.map(r => <option key={r.id} value={r.id}>{r.etichetta}</option>)}
          </select>
        </label>
        <label className="block text-sm">Confronto
          <select className={field} value={condizione.operator} onChange={e => setCondizione({ operator: e.target.value })}>
            {ammessi.map(o => <option key={o} value={o}>{operatori[o] || o}</option>)}
          </select>
        </label>
        <label className="block text-sm">Valore{campoCondizione?.unita ? ` (${campoCondizione.unita})` : ''}
          {numerico(campoCondizione)
            ? <input required type="number" className={field} value={condizione.value ?? ''}
                onChange={e => setCondizione({ value: e.target.value === '' ? '' : Number(e.target.value) })} />
            : <input required className={field} value={condizione.value ?? ''} placeholder="Es. pioggia"
                onChange={e => setCondizione({ value: e.target.value })} />}
        </label>
        <p className="text-xs text-muted sm:col-span-3">
          Se la condizione non vale, la regola si ferma lì: nessun messaggio, e nello storico trovi il perché.
          Un dato che non arriva non conta come zero: anche allora la regola si ferma.
        </p>
      </div>}
    </fieldset>

    <div className="rounded-lg border p-3 space-y-2">
      <p className="font-medium">Prova i dati</p>
      <p className="text-xs text-muted">{scrive
        ? 'Mostra i dati veri come sono adesso: non aggiunge e non cambia niente, non manda niente su WhatsApp e non salva la regola.'
        : 'Chiede i dati veri adesso, ma non manda niente su WhatsApp e non salva la regola.'}</p>
      {usaEsempio && <label className="block text-sm">Messaggio di esempio
        <input className={field} placeholder="Es. Roma domani" value={esempio} onChange={e => setEsempio(e.target.value)} />
      </label>}
      <button type="button" className="border rounded px-4 py-2" disabled={inCorso} onClick={provaDati}>
        {inCorso ? 'Chiedo i dati…' : 'Prova i dati'}
      </button>
      {errore && <p role="alert" className="text-sm text-warning">{errore}</p>}
      {prova && <div role="status" className="space-y-2 text-sm">
        {prova.condizione && <p className={prova.condizione.passa ? 'text-brand-700' : 'text-muted'}>
          {prova.condizione.passa ? 'Oggi la regola manderebbe il messaggio. ' : 'Oggi la regola non manderebbe niente. '}
          {prova.condizione.motivo}
        </p>}
        <p className="text-xs text-muted">Il messaggio sarebbe:</p>
        <p className="whitespace-pre-wrap rounded bg-surface-2 px-3 py-2">{prova.risultati.text}</p>
      </div>}
    </div>
  </div>;
}
